/* eslint-disable jsx-a11y/alt-text */

import React from 'react';
import PropTypes from 'prop-types';
import { withViewport } from '@prisjakt/ui-viewport';

class Lazy extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      shouldLoad: false,
      nodeRef: null,
    };

    this.abortIdle = null;
  }

  componentDidMount() {
    if (this.props.loadOnIdle && !this.state.shouldLoad) {
      this.scheduleLoadOnIdle();
    }
  }

  /**
   * Only re-render if visibility has changed, one of the non-own props that we pass down to the native <img> element has changed or if children has changed.
   * This is mainly to avoid re-rendering on every viewport update if nothing relevant has changed, since we receive new props on scroll.
   */
  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.state.shouldLoad !== nextState.shouldLoad ||
      this.state.nodeRef !== nextState.nodeRef ||
      this.props.loadOnIdle !== nextProps.loadOnIdle ||
      this.props.children !== nextProps.children
    );
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !prevProps.loadOnIdle &&
      this.props.loadOnIdle &&
      !this.state.shouldLoad
    ) {
      this.scheduleLoadOnIdle();
    }

    if (!prevState.shouldLoad && this.state.shouldLoad && this.abortIdle) {
      this.abortIdle();
      this.abortIdle = null;
    }
  }

  static getDerivedStateFromProps(props, state) {
    if (state.shouldLoad) {
      return state;
    }

    return { ...state, shouldLoad: Lazy.getVisibility(state.nodeRef, props) };
  }

  /**
   * Determines whether a rect is visible in the viewport (taking the configured offset into account)
   */
  static getVisibility(nodeRef, { offset, viewport }) {
    if (!nodeRef) {
      return false;
    }
    const { top, left, bottom, right } = nodeRef.getBoundingClientRect();

    if (top + right + bottom + left === 0) {
      return false;
    }

    const extendedViewport = {
      top: 0 - offset,
      left: 0 - offset,
      right: viewport.width + offset,
      bottom: viewport.height + offset,
    };

    return (
      bottom >= extendedViewport.top &&
      right >= extendedViewport.left &&
      top <= extendedViewport.bottom &&
      left <= extendedViewport.right
    );
  }

  scheduleLoadOnIdle() {
    if (process.env.NODE_ENV === 'test') {
      return;
    }

    if (window && window.requestIdleCallback) {
      this.abortIdle = cancelIdleCallback.bind(
        null,
        requestIdleCallback(() => this.setState({ shouldLoad: true }))
      );
    } else {
      this.abortIdle = clearTimeout.bind(
        null,
        setTimeout(() => this.setState({ shouldLoad: true }), 100)
      );
    }
  }

  setNodeRef = ref => {
    if (!this.state.nodeRef) {
      this.setState({ nodeRef: ref });
    }
  };

  render() {
    if (this.state.shouldLoad || process.env.NODE_ENV === 'test') {
      return this.props.children;
    }

    return React.cloneElement(this.props.placeholder, {
      ref: this.setNodeRef,
    });
  }
}

Lazy.propTypes = {
  /**
   * Pass one or more children to track
   */
  children: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]),
  placeholder: PropTypes.node,
  /**
   * A pixel offset to trigger the load when the image is within that distance of the viewport
   */
  offset: PropTypes.number,
  viewport: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
    left: PropTypes.number,
    top: PropTypes.number,
  }),
  loadOnIdle: PropTypes.bool,
};

Lazy.defaultProps = {
  children: null,
  placeholder: <span />,
  offset: 100,
  viewport: {
    width: 0,
    height: 0,
    left: 0,
    top: 0,
  },
  loadOnIdle: false,
};

export default withViewport(Lazy);
